/**
 * Savings Toast Component
 * 
 * This module handles the dynamic calculation and display of savings toasts
 * that show next to product prices. It integrates with both variant changes
 * and Seal Subscriptions price updates.
 * 
 * Features:
 * - Real-time savings calculation (percentage and dollar amounts)
 * - Dynamic show/hide based on savings availability
 * - Integration with ProductPrice and Seal Subscriptions events
 * - Currency formatting matching theme settings
 * - Responsive design and customizable styling
 */

class SavingsToast {
  constructor() {
    this.debug = false; // Set to true for debugging
    this.toastElements = new Map(); // Cache toast elements
    this.originalData = new Map(); // Store original price data
    this.initialized = false;
    
    this.init();
  }

  /**
   * Initialize the savings toast system
   */
  init() {
    if (this.initialized) return;
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupToasts());
    } else {
      this.setupToasts();
    }
    
    this.setupEventListeners();
    this.initialized = true;
  }

  /**
   * Set up event listeners for price updates
   */
  setupEventListeners() {
    // Note: Horizon's variant update path is handled via the 'price:updated' listener below.
    // product-price.js replaces the price DOM on variant:update and dispatches 'price:updated',
    // which triggers reinitializeToasts(). No direct variant:update listener is needed here.

    // Listen for custom price update events (from Seal Subscriptions integration)
    document.addEventListener('savings-toast:update', (event) => {
      this.handleCustomPriceUpdate(event);
    });

    // Listen for restore events (from Seal Subscriptions integration)
    document.addEventListener('savings-toast:restore', (event) => {
      this.handleRestore(event);
    });

    // Listen for hide events (from Seal Subscriptions integration)
    document.addEventListener('savings-toast:hide', (event) => {
      this.hideAllToasts();
    });

    // Listen for the price:updated event from product-price.js (variant changes)
    document.addEventListener('price:updated', () => {
      this.reinitializeToasts();
    });

    // Listen for the price:restored event from seal-subscriptions-price-integration.js (one-time purchase restore)
    document.addEventListener('price:restored', () => {
      this.reinitializeToasts();
    });
  }

  /**
   * Find and set up all savings toast elements on the page
   */
  setupToasts() {
    const toastElements = document.querySelectorAll('[ref="savingsToast"]');
    
    toastElements.forEach((toast, index) => {
      const key = `toast-${index}`;
      const priceContainer = toast.closest('[ref="priceContainer"]');
      
      if (priceContainer) {
        // Toast is now inside priceContainer again, but positioned absolutely
        const productPrice = priceContainer.closest('product-price');
        
        this.toastElements.set(key, {
          toast,
          priceContainer,
          productPrice
        });
        
        // Store original data
        this.storeOriginalData(key, toast);
        
        // Initial visibility check
        this.updateToastVisibility(key);
      }
    });
  }

  /**
   * Store original price data from toast element
   * @param {string} key - Toast identifier
   * @param {HTMLElement} toast - Toast element
   */
  storeOriginalData(key, toast) {
    const originalData = {
      savingsType: toast.dataset.savingsType,
      savingsAmount: parseInt(toast.dataset.savingsAmount),
      savingsPercentage: parseInt(toast.dataset.savingsPercentage),
      compareAtPrice: parseInt(toast.dataset.compareAtPrice),
      currentPrice: parseInt(toast.dataset.currentPrice),
      timestamp: Date.now()
    };
    
    this.originalData.set(key, originalData);
  }

  /**
   * Handle variant update events from ProductPrice component
   * @param {CustomEvent} event - Variant update event
   */
  handlePriceUpdate(event) {
    try {
      const { data } = event.detail || {};
      if (!data || !data.html) return;

      const newPriceContainer = data.html.querySelector('[ref="priceContainer"]');
      if (!newPriceContainer) return;

      const newToast = newPriceContainer.querySelector('[ref="savingsToast"]');

      // Scope the update to the product that actually changed. The event
      // originates from a specific product-price element (event.target) and/or
      // carries the productId in detail.data. Only update cached toasts whose
      // owning product-price matches; otherwise PDP pages with multiple
      // product-price blocks (e.g. main + sticky) would all clobber each other.
      const eventProductId = data.productId
        ? String(data.productId)
        : (event.target instanceof HTMLElement && event.target.dataset && event.target.dataset.productId)
          ? String(event.target.dataset.productId)
          : null;

      this.toastElements.forEach((container, key) => {
        const ownerProductPrice = container.productPrice;
        const ownerProductId = ownerProductPrice && ownerProductPrice.dataset
          ? String(ownerProductPrice.dataset.productId || '')
          : '';

        if (eventProductId && ownerProductId && ownerProductId !== eventProductId) {
          return;
        }

        if (newToast) {
          this.updateToastFromElement(key, newToast);
        } else {
          this.hideToast(key);
        }
      });

    } catch (error) {
      console.error('Error handling price update:', error);
    }
  }

  /**
   * Handle custom price update events (from Seal Subscriptions)
   * @param {CustomEvent} event - Custom price update event
   */
  handleCustomPriceUpdate(event) {
    try {
      const { price, compareAtPrice, source } = event.detail;
      
      if (!price || !compareAtPrice || compareAtPrice <= price) {
        this.hideAllToasts();
        return;
      }
      
      // Update all toasts with new pricing
      this.toastElements.forEach((container, key) => {
        this.updateToastWithPrices(key, price, compareAtPrice);
      });
      
    } catch (error) {
      console.error('Error handling custom price update:', error);
    }
  }

  /**
   * Handle restore events (restore original pricing)
   * @param {CustomEvent} event - Restore event
   */
  handleRestore(event) {
    try {
      // Restore all toasts to their original data
      this.toastElements.forEach((container, key) => {
        const originalData = this.originalData.get(key);
        if (originalData) {
          this.updateToastWithPrices(key, originalData.currentPrice, originalData.compareAtPrice);
        }
      });
      
    } catch (error) {
      console.error('Error handling restore:', error);
    }
  }

  /**
   * Update toast from another toast element (for variant updates)
   * @param {string} key - Toast identifier
   * @param {HTMLElement} sourceToast - Source toast element with updated data
   */
  updateToastFromElement(key, sourceToast) {
    const currentPrice = parseInt(sourceToast.dataset.currentPrice);
    const compareAtPrice = parseInt(sourceToast.dataset.compareAtPrice);
    
    if (currentPrice && compareAtPrice && compareAtPrice > currentPrice) {
      this.updateToastWithPrices(key, currentPrice, compareAtPrice);
    } else {
      this.hideToast(key);
    }
  }

  /**
   * Update toast with new pricing information
   * @param {string} key - Toast identifier
   * @param {number} currentPrice - Current price in cents
   * @param {number} compareAtPrice - Compare at price in cents
   */
  updateToastWithPrices(key, currentPrice, compareAtPrice) {
    const container = this.toastElements.get(key);
    if (!container) {
      return;
    }
    
    const { toast } = container;
    const savingsType = toast.dataset.savingsType || 'percentage';
    
    // Calculate savings
    const savingsAmount = compareAtPrice - currentPrice;
    const savingsPercentage = Math.round((savingsAmount / compareAtPrice) * 100);
    
    // Update data attributes
    toast.dataset.savingsAmount = savingsAmount;
    toast.dataset.savingsPercentage = savingsPercentage;
    toast.dataset.compareAtPrice = compareAtPrice;
    toast.dataset.currentPrice = currentPrice;
    
    // Update display content
    const amountSpan = toast.querySelector('.savings-toast__amount');
    
    if (amountSpan) {
      if (savingsType === 'percentage') {
        amountSpan.textContent = `${savingsPercentage}%`;
      } else {
        const formattedAmount = this.formatPrice(savingsAmount);
        amountSpan.textContent = formattedAmount;
      }
      
      // Force a visual refresh
      amountSpan.style.opacity = '0.99';
      setTimeout(() => {
        amountSpan.style.opacity = '';
      }, 1);
    }
    
    this.showToast(key);
  }

  /**
   * Show a toast
   * @param {string} key - Toast identifier
   */
  showToast(key) {
    const container = this.toastElements.get(key);
    if (container) {
      container.toast.style.display = '';
      container.toast.setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Hide a toast
   * @param {string} key - Toast identifier
   */
  hideToast(key) {
    const container = this.toastElements.get(key);
    if (container) {
      container.toast.style.display = 'none';
      container.toast.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * Hide all toasts
   */
  hideAllToasts() {
    this.toastElements.forEach((container, key) => {
      this.hideToast(key);
    });
  }

  /**
   * Re-initializes the toasts after a DOM update.
   */
  reinitializeToasts() {
    this.toastElements.clear();
    this.originalData.clear();
    this.setupToasts();
  }

  /**
   * Update toast visibility based on savings availability
   * @param {string} key - Toast identifier
   */
  updateToastVisibility(key) {
    const container = this.toastElements.get(key);
    if (!container) return;
    
    const { toast } = container;
    const currentPrice = parseInt(toast.dataset.currentPrice);
    const compareAtPrice = parseInt(toast.dataset.compareAtPrice);
    
    if (compareAtPrice && currentPrice && compareAtPrice > currentPrice) {
      this.showToast(key);
    } else {
      this.hideToast(key);
    }
  }

  /**
   * Format price for display
   * @param {number} priceInCents - Price in cents
   * @returns {string} Formatted price string
   */
  formatPrice(priceInCents) {
    try {
      if (typeof window.Shopify !== 'undefined' && window.Shopify.money_format) {
        // Use Shopify's money formatting
        const priceInDollars = priceInCents / 100;
        return window.Shopify.formatMoney(priceInCents, window.Shopify.money_format);
      } else {
        // Fallback formatting
        const priceInDollars = priceInCents / 100;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD'
        }).format(priceInDollars);
      }
    } catch (error) {
      console.error('Error formatting price:', error);
      return `$${(priceInCents / 100).toFixed(2)}`;
    }
  }

  /**
   * Enable debug logging
   */
  enableDebug() {
    this.debug = true;
  }

  /**
   * Disable debug logging
   */
  disableDebug() {
    this.debug = false;
  }

  /**
   * Log debug messages (only when debug is enabled)
   * @param {...any} args - Arguments to log
   */
  log(...args) {
    if (this.debug) {
      console.log('[Savings Toast]', ...args);
    }
  }

  /**
   * Log error messages
   * @param {...any} args - Arguments to log
   */
  error(...args) {
    console.error('[Savings Toast]', ...args);
  }
}

// Initialize the savings toast system when the script loads
if (typeof window !== 'undefined') {
  // Initialize on DOM ready or immediately if already ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.savingsToast = new SavingsToast();
    });
  } else {
    window.savingsToast = new SavingsToast();
  }
}